import {property} from '@wonderlandengine/api/decorators.js';
import {LevelSelect} from '../level-select.js';
import {Puzzle, PuzzleConstants} from '../puzzle.js';
import {GameRules} from './game-rules.js';
import {LineSpawnerNormal} from '../line-spawner-normal.js';
import {Object3D} from '@wonderlandengine/api';
import {CustomLogTag} from '../../classes/Constants.js';
import {FailStateNames} from '../../enums/FailStateNames.js';
import {ScoreController} from '../score-controller.js';
import {BlobInjector} from '../blob-injector.js';
import {SegmentedMusicPlayer, Urgency} from '../segmented-music-player.js';
import {UrgencyLevel} from '../../enums/UrgencyLevel.js';
import {Statistics} from '../statistics.js';
import {StatNames} from '../../enums/StatNames.js';
import {GameFlowController} from '../game-flow-controller.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {Localization} from '../../utils/Localization.js';
import {AudioDirector} from '../audio-director.js';
import {Sounds} from '../../enums/Sounds.js';
import {Analytics, AnalyticsEvent} from '../../classes/Analytics.js';
import {waitForSeconds} from '../../classes/coroutines/YieldInstructions.js';
import {CoroutineManager} from '../../classes/coroutines/CoroutineManager.js';
import {Coroutine} from '../../classes/coroutines/Coroutine.js';
import {FXPrefabs} from '../fx-prefabs.js';
import {vec3} from 'gl-matrix';
import {WLUtils} from '../../utils/WLUtils.js';
import {SpawnScoreAfterSeconds} from '../spawn-score-after-seconds.js';
import {BoardColor, BoardObject} from '../board-object.js';
import {DataManager} from '../../classes/DataManager.js';
import {PieceData, TutorialPiecesData} from '../../types/Tut1Pieces.js';
import {BlobPrefabs} from '../blob-prefabs.js';
import {FruitPrefabs} from '../fruit-prefabs.js';

const tempvec: vec3 = vec3.create();

export class DefaultGameRules extends GameRules {
    static TypeName = 'default-game-rules';
    static InheritProperties = true;

    @property.int(5)
    numberOfRowsAtStart = 5;

    @property.int(3)
    numberOfFruitColors = 3;

    @property.int(3)
    moveUpTurns = 3;

    @property.int(100)
    levelUpTurns = 100;

    @property.object({required: true})
    lineSpawnerObject!: Object3D;

    @property.int(0)
    levelUpTurnsIncrement = 0;

    @property.float(50)
    dropSpeedMax = 50;

    @property.float(1.25)
    dropSpeedMultiplierAfterAreaChange = 1.25;

    @property.float(1)
    dropSpeedDivisorAfterAreaChange = 1;

    @property.float(1.5)
    dropSpeedIncreaseFactor = 1.5;

    @property.int(9)
    heightToShowColumnWarning = 9;

    @property.int(6)
    heightToSlowMusic = 6;

    @property.int(100000)
    completionBonusScore = 100000;

    @property.string('ScoreCompletionBonus')
    completeBonusObject = 'ScoreCompletionBonus';

    private _levelSelectController: LevelSelect;

    private _turnCount: number;
    private _numberOfRowsForLevel: number;
    private _forceMoveUpTurn: number;
    private _isCoconutEmergency: boolean;
    private _coroutineManager: CoroutineManager = new CoroutineManager();
    private _tutorialSpawnSequence: string[] = [
        'Fruit Orange',
        'Fruit Green',
        'Fruit Blue',
    ];

    protected _timeToLevelUp: boolean;
    protected _currentMoveUpTurns: number;
    protected _puzzle: Puzzle;
    protected _currentLevelUpTurns: number;
    protected _turnsSinceLastLevelUp: number;
    protected _lineSpawner: LineSpawnerNormal;
    protected _reachedMaxLevel: boolean;
    protected _currentUrgency: UrgencyLevel;
    protected _lastHeightUrgency: UrgencyLevel;
    protected _tutorialPieces: PieceData[] = [];

    public tutorialInProgress = false;

    public override start() {
        super.start();

        if (this.isFirstGame) {
            this.tutorialInProgress = true;
            DataManager.instance
                .loadData<TutorialPiecesData>('data/Tut1Pieces.bin')
                .then((data) => {
                    this._tutorialPieces.push(...data.pieces);
                });
        }

        //audioDirector = AudioDirector.GetDirector();
        this._levelSelectController = LevelSelect.instance;
        this._puzzle = Puzzle.instance;
        this._lineSpawner = this.lineSpawnerObject.getComponent(LineSpawnerNormal);
    }

    onActivate(): void {
        if (!this._puzzle) {
            this._puzzle = Puzzle.instance;
        }
        this._puzzle.registerResetGameCallback(this.resetGame);
    }

    onDeactivate(): void {
        this._puzzle.unregisterResetGameCallback(this.resetGame);
    }

    update(delta: number): void {
        this._coroutineManager.update(delta);
    }

    override startNewGame() {
        this._isCoconutEmergency = false;

        super.startNewGame();

        if (this.isFirstGame) {
            this.tutorialInProgress = true;
        } else {
            this.tutorialInProgress = false;
        }
        this._puzzle.score.showShapeCount(this._numShapeBonus);

        this._reachedMaxLevel = false;

        this._timeToLevelUp = false;

        this._turnCount = 0;
        this._currentMoveUpTurns = this.moveUpTurns;

        this._turnsSinceLastLevelUp = 0;
        this._currentLevelUpTurns = this.levelUpTurns;

        this._numberOfRowsForLevel = this.numberOfRowsAtStart;

        if (this.isFirstGame) {
            for (let x = 0; x < 6; x++) {
                Puzzle.instance.addRow();
            }

            // spawn pre-populated stacks here
            for (let x = 0; x < this._tutorialPieces.length; x++) {
                // 	GameObject pieceObject = Instantiate(pieces[x].piecePrefab, Vector3.zero, Quaternion.identity) as GameObject;
                const pieceObject = BlobPrefabs.instance.spawn(
                    this._tutorialPieces[x].piecePrefab,
                    null,
                    false
                );
                const boardObj = pieceObject.getComponent(BoardObject);
                Puzzle.instance.addObject(
                    boardObj,
                    this._tutorialPieces[x].row,
                    this._tutorialPieces[x].column
                );
                boardObj.place();
                if (this._tutorialPieces[x].specialToInject) {
                    // 		GameObject payload = Instantiate(pieces[x].specialToInject, Vector3.zero, pieceObject.transform.rotation) as GameObject;
                    const payload = BlobPrefabs.instance.spawn(
                        this._tutorialPieces[x].specialToInject,
                        null,
                        false
                    );
                    boardObj.loadObject(payload);
                }
            }
            // for (let x = 0; x < this._numberOfRowsForLevel; x++) {
            //     this._lineSpawner.spawnNewLine(this._puzzle);
            // }
        } else {
            for (let x = 0; x < this._numberOfRowsForLevel; x++) {
                this._lineSpawner.spawnNewLine(this._puzzle);
            }
        }

        const injectors = this._lineSpawner.object.getComponents(BlobInjector);
        for (let x = 0; x < injectors.length; x++) {
            injectors[x].startGame(this.puzzleController);
        }

        Statistics.instance.recordAdditive(StatNames.NumberLevels, 1);
    }

    /**
     * Callback for when the game is reset
     * @virtual
     * @param puzzlecontroller The puzzle controller
     */
    resetGame = (puzzlecontroller: Puzzle) => {
        // if (moveUpWarningSFX)
        // {
        //     audioDirector.StopLooping(moveUpWarningSFX);
        // }
    };

    override timeToChangeStack(): boolean {
        return this._checkStackChange() && !this.isCoconutEmergency();
    }

    override changeStack(): boolean {
        if (this.tutorialInProgress) {
            return;
        }
        // if (moveUpWarningSFX) {
        //     audioDirector.StopLooping(moveUpWarningSFX);
        // }

        if (
            this._turnCount >= this._currentMoveUpTurns ||
            (this._forceMoveUpTurn > 0 && this._turnCount > this._forceMoveUpTurn)
        ) {
            this._lineSpawner.spawnNewLine(this._puzzle);
            AudioDirector.instance.playOneShot(Sounds.moveUp, true);
        }

        this._turnCount = 0;
        this._forceMoveUpTurn = 0;

        if (this._timeToLevelUp && !this._isGameOver()) {
            this.levelUpSequence();
        }

        return true;
    }

    override detectGameOver(): boolean {
        const stackFull = this._puzzle.stackFull();

        if (stackFull) {
            this.failStateCause = FailStateNames.FruitOverflow;
            const columnCount = this._puzzle.lanes.length;
            const topRow = PuzzleConstants.rowCount - 1;
            for (let x = 0; x < columnCount; x++) {
                const obj = this._puzzle.getObject(topRow, x);
                if (obj && !obj.isFruit) {
                    this.failStateCause = FailStateNames.BlobsOverflow;
                    break;
                }
            }
        }

        const gameOver =
            stackFull || super.detectGameOver() || this._reachedMaxLevel == true;

        // if (gameOver && moveUpWarningSFX) {
        //     // safety
        //     audioDirector.StopLooping(moveUpWarningSFX);
        // }

        if (gameOver) {
            if (this._reachedMaxLevel == false) {
                ScoreController.instance.showGameOverText();
            } else {
                ScoreController.instance.showMaxedLevelText();
            }
        }

        return gameOver;
    }

    /**
     * @virtual
     */
    levelUpSequence() {
        let level = this._puzzle.level;

        level++;

        this._puzzle.level = level;

        this._currentLevelUpTurns += this.levelUpTurnsIncrement;
        this._turnsSinceLastLevelUp = 0;

        // // Determine what a new level does
        const resetBoard = this.levelUp(true);

        this._timeToLevelUp = false;

        if (resetBoard) {
            this._levelSelectController.nextEnvironment();
        }
    }

    public levelUp(showText: boolean): boolean {
        let level = this._puzzle.level;

        let resetBoard = false;

        // YOU WIN = GAME OVER
        if (level == this.maxLevelToWin && this.maxLevelToWin > 0) {
            this._coroutineManager.addCoroutine(
                new Coroutine(this._spawnCompletionBonus())
            );

            // if (QualityControlManager.GetInstance().Quality == QualityControlManager.DeviceQuality.Normal)
            // {
            // 	spawnedFireworks = Instantiate(beatGameFX, new Vector3(0,0.8f, 0), Quaternion.identity) as GameObject;
            // }
            AudioDirector.instance.playOneShotWithPriority(Sounds.beatGameSFX);
            //AudioDirector.instance.playLooping(Sounds.beatGameLoopSFX);
            //StartCoroutine("CleanUpFireworks");
            this.beatGame();
            this.failStateCause = FailStateNames.MaxLevelReached;
            this._reachedMaxLevel = true;
            return resetBoard;
        }

        //Achievement sunrise
        if (Statistics.instance.getLong(StatNames.SunriseAchievement) == 0) {
            if (
                GameFlowController.instance.gameMode == GameModeType.Normal &&
                level >= 10
            ) {
                Statistics.instance.record(StatNames.SunriseAchievement, 1);
            }
        }

        Statistics.instance.recordAdditive(StatNames.NumberLevels, 1);

        const levelUp = level % 5;

        let levelUpFXTextString = '';

        if (this.dropSpeedMax == 0) {
            this.engine.log.error(CustomLogTag.Game, 'drop speed max is 0!!');
        }

        switch (levelUp) {
            case 0:
                if (this.dropSpeedDivisorAfterAreaChange == 0) {
                    this.dropSpeedDivisorAfterAreaChange = 1;
                }
                this._puzzle.dropSpeedFactor = Math.max(
                    this._puzzle.dropSpeedFactor / this.dropSpeedDivisorAfterAreaChange,
                    1.0
                );
                resetBoard = this.resetBoard();

                AudioDirector.instance.playOneShot(Sounds.levelUp, true);

                if (resetBoard == true) {
                    //levelUpFXTextString = Localization.instance.Get('AreaUp');
                } else {
                    //levelUpFXTextString = Localization.instance.Get('SlowDown');
                }

                this.dropSpeedIncreaseFactor *= this.dropSpeedMultiplierAfterAreaChange;
                break;
            case 1:
                this._puzzle.dropSpeedFactor = Math.min(
                    this._puzzle.dropSpeedFactor + this.dropSpeedIncreaseFactor,
                    this.dropSpeedMax
                );
                // levelUpFXTextString = Localization.instance.Get('SpeedUp');
                AudioDirector.instance.playOneShot(Sounds.speedUp, true);
                break;
            case 2:
                this._puzzle.dropSpeedFactor = Math.min(
                    this._puzzle.dropSpeedFactor + this.dropSpeedIncreaseFactor,
                    this.dropSpeedMax
                );
                // levelUpFXTextString = Localization.instance.Get('SpeedUp');
                AudioDirector.instance.playOneShot(Sounds.speedUp, true);
                break;
            case 3:
                this._puzzle.dropSpeedFactor = Math.min(
                    this._puzzle.dropSpeedFactor + this.dropSpeedIncreaseFactor,
                    this.dropSpeedMax
                );
                // levelUpFXTextString = Localization.instance.Get('SpeedUp');
                AudioDirector.instance.playOneShot(Sounds.speedUp, true);
                break;
            case 4:
                this._puzzle.dropSpeedFactor = Math.min(
                    this._puzzle.dropSpeedFactor + this.dropSpeedIncreaseFactor,
                    this.dropSpeedMax
                );
                // levelUpFXTextString = Localization.instance.Get('SpeedUp');
                AudioDirector.instance.playOneShot(Sounds.speedUp, true);
                break;

            default:
                levelUpFXTextString = '';
                break;
        }

        if (showText == true) {
            //PuzzleController.scoreController.LevelUpText(levelUpFXTextString);
        }

        // huge LEVEL UP text in the center
        if (level + 1 == this.maxLevelToWin && this.maxLevelToWin > 0) {
            //PuzzleController.scoreController.LastWaveBigText();
        } else {
            //PuzzleController.scoreController.LevelUpBigText();
        }

        return resetBoard;
    }

    override warnStackChange(): boolean {
        const warningTime =
            this._turnCount + 1 == this._currentMoveUpTurns ||
            (this._forceMoveUpTurn > 0 && this._turnCount >= this._forceMoveUpTurn);
        // if (warningTime && moveUpWarningSFX)
        // {
        // 	audioDirector.PlayLooping(moveUpWarningSFX);
        // }
        return warningTime;
    }

    /**
     * @virtual
     */
    override turnComplete() {
        super.turnComplete();

        this._turnCount++;
        if (this.tutorialInProgress) {
            return;
        }
        this._turnsSinceLastLevelUp++;

        this.setCoconutEmergency(this.checkCoconutEmergency());

        const forceMinimumStack = this.forceMinimumStack();

        if (this._forceMoveUpTurn == 0 && forceMinimumStack) {
            this._forceMoveUpTurn = this._turnCount;
        }

        // calculate level bar on hud
        this.calculateLevelBar();

        if (this._turnsSinceLastLevelUp >= this._currentLevelUpTurns) {
            this._timeToLevelUp = true;
        }

        this.showWarning(this.calculateUrgency());
    }

    protected setCoconutEmergency(val: boolean) {
        this._isCoconutEmergency = val;
    }

    override checkCoconutEmergency() {
        return (
            (this._checkStackChange() &&
                !this.isCoconutEmergency() &&
                Puzzle.instance.spawner.lastPieceWasBomb &&
                this.getHighestOccupiedRow(true) == PuzzleConstants.rowCount - 2) ||
            super.checkCoconutEmergency()
        );
    }
    /**
     * @virtual
     */
    calculateLevelBar() {
        // figure out where level bar should be
        const levelValue = this._turnsSinceLastLevelUp / this._currentLevelUpTurns;

        this.puzzleController.score.adjustLevelSlider(levelValue);
    }

    /**
     *
     * @virtual
     */
    forceMinimumStack(): boolean {
        return this.getHighestOccupiedRow(false) < this.numberOfRowsAtStart - 1;
    }

    /**
     * @virtual
     */
    showWarning(urgency: UrgencyLevel) {
        Puzzle.instance.score.showColumnWarning(urgency == UrgencyLevel.VeryHigh);
        SegmentedMusicPlayer.instance.setUrgency(
            urgency != UrgencyLevel.Low ? Urgency.Urgent : Urgency.Normal
        );
        this._currentUrgency = urgency;
    }

    /**
     * @virtual
     */
    calculateUrgency(): UrgencyLevel {
        // get highest column
        const height = this.getHighestOccupiedRow(true);

        let urgency = this._lastHeightUrgency;

        //are you above highest row? if so, show warning
        if (height >= this.heightToShowColumnWarning) {
            urgency = UrgencyLevel.VeryHigh;
        } else if (height <= this.heightToSlowMusic) {
            urgency = UrgencyLevel.Low;
        } else if (this._lastHeightUrgency == UrgencyLevel.VeryHigh) {
            urgency = UrgencyLevel.High;
        }

        this._lastHeightUrgency = urgency;

        return urgency;
    }

    /**
     *
     * @virtual
     */
    resetBoard(): boolean {
        return true;
    }

    override showFinalResults() {
        const combo = this.puzzleController.score.combo;
        this.puzzleController.score.showFinalResults(
            Localization.instance.get('BestChainFormat'),
            combo,
            Localization.instance.get('Normal'),
            StatNames.RecentHighScore
        );
    }

    override saveHighScore(score: number): void {
        Statistics.instance.recordHighest(StatNames.HighScore, score);
    }

    override saveCumulativeScore(score: number): void {
        Statistics.instance.recordAdditive(StatNames.CumulativeScoreAny, score);

        const roundedScore = (() => {
            if (score > 10000000) {
                return score - (score % 1000000);
            } else if (score > 1000000) {
                return score - (score % 1000000);
            } else if (score > 100000) {
                return score - (score % 100000);
            } else if (score > 10000) {
                return score - (score % 10000);
            } else {
                return score - (score % 1000);
            }
        })();

        Analytics.LogEventWithValue(AnalyticsEvent.ScoreExpert, roundedScore);
    }

    protected getHighestOccupiedRow(countFruit: boolean): number {
        const columnCount = this._puzzle.lanes.length;
        let maxHeight = 0;
        for (let x = 0; x < columnCount; x++) {
            const row = this._puzzle.getHighestOccupiedRow(x);
            if (row > maxHeight) {
                if (countFruit) {
                    maxHeight = row;
                } else {
                    for (let y = row; y > maxHeight; y--) {
                        const obj = this._puzzle.getObject(y, x);
                        if (obj && !obj.isFruit) {
                            maxHeight = y;
                            break;
                        }
                    }
                }
            }
        }
        return maxHeight;
    }

    override getHighScore(): number {
        return Statistics.instance.getLong(StatNames.HighScore);
    }

    override getWeeklyHighScore(): number {
        return Statistics.instance.getLong(StatNames.RecentHighScore);
    }

    override fruitPreSpawn(obj: Object3D): Object3D {
        super.fruitPreSpawn(obj);

        if (this.isFirstGame && this._tutorialSpawnSequence.length > 0) {
            // for tutorial, force the first three pieces
            const pieceName = this._tutorialSpawnSequence.shift();
            if (pieceName) {
                const piece = FruitPrefabs.instance.spawn(pieceName, null, false);
                return piece;
            }
        }
        return obj;
    }

    /**
     * @virtual
     */
    beatGame() {}

    private _checkStackChange(): boolean {
        return (
            this._turnCount >= this._currentMoveUpTurns ||
            this._timeToLevelUp == true ||
            (this._forceMoveUpTurn > 0 && this._turnCount > this._forceMoveUpTurn)
        );
    }

    /**
     * figures out if the game is over or not but doesn't change anything.
     */
    private _isGameOver(): boolean {
        const stackFull = this._puzzle.stackFull();
        const gameOver = stackFull || super.detectGameOver();
        return gameOver;
    }

    private *_spawnCompletionBonus() {
        yield waitForSeconds(0.65);

        const targetCamera = this.engine.scene.activeViews[0].object;
        const scoreFX = FXPrefabs.instance.spawn(
            this.completeBonusObject,
            targetCamera,
            false
        );
        Puzzle.instance.score.centerParent.getPositionWorld(tempvec);
        scoreFX.setPositionWorld(tempvec);
        const shapeParams = scoreFX.getComponent(SpawnScoreAfterSeconds);
        shapeParams.setTextOfThis('CompletionBonus');
        shapeParams.setScoreToShow(this.completionBonusScore);
        shapeParams.setParent(Puzzle.instance.score.centerParent);
        shapeParams.setColor(BoardColor.Yellow);

        WLUtils.setActive(scoreFX, true);
    }
}
